import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';
import { Calendar, ChevronDown, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import css from './date-range-picker.module.scss';
import { RangeCalendar } from './range-calendar';

const styles = pickCss(css, css.root);

export type DateRangePreset =
  | 'today'
  | 'tomorrow'
  | 'oneWeek'
  | 'twoWeeks'
  | 'oneMonth'
  | 'twoMonth'
  | 'activeWeek'
  | 'custom';

// Human labels match the legacy operations dropdown verbatim.
const PRESET_LABEL: Record<DateRangePreset, string> = {
  today: 'Today',
  tomorrow: 'Tomorrow',
  oneWeek: 'One Week',
  twoWeeks: 'Two Weeks',
  oneMonth: 'One Month',
  twoMonth: 'Two Month',
  activeWeek: 'Active Week',
  custom: 'Custom',
};

// Default preset set — preserves the pre-existing pickers (inbox, itineraries).
const DEFAULT_PRESETS: DateRangePreset[] = ['oneWeek', 'activeWeek', 'custom'];

// Legacy operations dropdown (TCO:01): Today / Tomorrow / One Week / Two Weeks
// / One Month / Two Month, plus Custom.
export const OPERATIONS_PRESETS: DateRangePreset[] = [
  'today',
  'tomorrow',
  'oneWeek',
  'twoWeeks',
  'oneMonth',
  'twoMonth',
  'custom',
];

export type DateRangeValue = {
  startDate?: string;
  endDate?: string;
};

type DateRangePickerProps = {
  className?: string;
  disabled?: boolean;
  onChange?: (value: DateRangeValue) => void;
  // Trigger text shown when no range is selected. Defaults to 'Date range';
  // callers pass a custom label to distinguish multiple pickers (e.g. a
  // travel-date range vs a created-date range in the same filter panel).
  placeholder?: string;
  presets?: DateRangePreset[];
  // When set, editing one custom endpoint shifts the other to keep the same
  // day-count (the range translates rather than resizing). Used by the
  // itinerary settings, where the trip's duration is fixed by its day list and
  // the picker only moves it. Off by default — filters set endpoints freely.
  preserveDuration?: boolean;
  // Render an inline month calendar that highlights the whole start→end span,
  // so the duration is visible (the native From/To inputs only mark a single
  // day). Clicking a day moves the window when `preserveDuration` is on, else
  // builds the range with two clicks. Off by default.
  showCalendar?: boolean;
  size?: 'sm' | 'default';
  style?: CSSProperties;
  value?: DateRangeValue;
};

const toUtcDayStart = (date: Date) => {
  const next = new Date(date);
  next.setUTCHours(0, 0, 0, 0);
  return next;
};

const toUtcDayEnd = (date: Date) => {
  const next = new Date(date);
  next.setUTCHours(23, 59, 59, 999);
  return next;
};

const range = (start: Date, end: Date): DateRangeValue => ({
  startDate: toUtcDayStart(start).toISOString(),
  endDate: toUtcDayEnd(end).toISOString(),
});

// `value` is the current range — duration presets anchor their window on the
// existing start date when one is set ("date set from start date"), else today.
const computePreset = (
  preset: DateRangePreset,
  value: DateRangeValue
): DateRangeValue => {
  if (preset === 'custom') return {};

  const todayStart = toUtcDayStart(new Date());
  const anchor = value.startDate
    ? toUtcDayStart(new Date(value.startDate))
    : todayStart;
  const plusDays = (from: Date, days: number) => {
    const next = new Date(from);
    next.setUTCDate(next.getUTCDate() + days);
    return next;
  };
  const plusMonths = (from: Date, months: number) => {
    const next = new Date(from);
    next.setUTCMonth(next.getUTCMonth() + months);
    return next;
  };

  switch (preset) {
    case 'today':
      return range(todayStart, todayStart);
    case 'tomorrow': {
      const tomorrow = plusDays(todayStart, 1);
      return range(tomorrow, tomorrow);
    }
    case 'oneWeek':
      return range(anchor, plusDays(anchor, 7));
    case 'twoWeeks':
      return range(anchor, plusDays(anchor, 14));
    case 'oneMonth':
      return range(anchor, plusMonths(anchor, 1));
    case 'twoMonth':
      return range(anchor, plusMonths(anchor, 2));
    default: {
      // activeWeek: current ISO week (Mon–Sun) in UTC
      const weekStart = new Date(todayStart);
      const dayOfWeek = weekStart.getUTCDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      weekStart.setUTCDate(weekStart.getUTCDate() + mondayOffset);
      return range(weekStart, plusDays(weekStart, 6));
    }
  }
};

// Long-form display matching the legacy v2 picker: `Jun 08, 2026`. Parse the
// ISO string's leading `YYYY-MM-DD` directly (UTC wall-clock) so there's no
// timezone drift — no `new Date(...)` round-trip that could shift the day.
const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatDisplay = ({ startDate, endDate }: DateRangeValue) => {
  if (!(startDate && endDate)) return null;
  const fmt = (iso: string) => {
    const [y, m, d] = iso.slice(0, 10).split('-');
    const month = MONTH_ABBR[Number(m) - 1];
    if (!month) return iso.slice(0, 10);
    return `${month} ${d}, ${y}`;
  };
  return `${fmt(startDate)} – ${fmt(endDate)}`;
};

const isoToInputValue = (iso?: string) => (iso ? iso.slice(0, 10) : '');

export const DateRangePicker = ({
  className,
  disabled,
  onChange,
  placeholder = 'Date range',
  presets = DEFAULT_PRESETS,
  preserveDuration = false,
  showCalendar = false,
  size = 'default',
  style,
  value = {},
}: DateRangePickerProps) => {
  const [open, setOpen] = useState(false);
  const [activePreset, setActivePreset] = useState<DateRangePreset>('custom');
  const rootRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Horizontal shift applied after render to keep the dropdown within the viewport.
  const [shift, setShift] = useState(0);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Dropdown opens from left: 0 (trigger's left edge). If the right edge
  // overflows the viewport, shift left just enough to stay in-bounds.
  // useLayoutEffect fires before paint so there's no visible flash.
  // A resize listener re-runs the check while the dropdown is open.
  useLayoutEffect(() => {
    if (!open) {
      setShift(0);
      return;
    }
    const reposition = () => {
      const el = dropdownRef.current;
      if (!el) return;
      const MARGIN = 8;
      const rect = el.getBoundingClientRect();
      if (rect.right > window.innerWidth - MARGIN) {
        setShift(window.innerWidth - MARGIN - rect.right);
      } else {
        setShift(0);
      }
    };
    reposition();
    window.addEventListener('resize', reposition);
    return () => window.removeEventListener('resize', reposition);
  }, [open]);

  const applyPreset = (preset: DateRangePreset) => {
    setActivePreset(preset);
    if (preset !== 'custom') {
      onChange?.(computePreset(preset, value));
      setOpen(false);
    }
  };

  const handleCustomChange = (key: 'startDate' | 'endDate', iso: string) => {
    const next: DateRangeValue = {
      startDate: value.startDate,
      endDate: value.endDate,
    };
    if (!iso) {
      next[key] = undefined;
    } else {
      const date = new Date(`${iso}T00:00:00.000Z`);
      next[key] =
        key === 'startDate'
          ? toUtcDayStart(date).toISOString()
          : toUtcDayEnd(date).toISOString();
    }
    // Duration-preserving mode (itinerary): translate the OTHER endpoint by the
    // same span so the day-count is unchanged — moving the trip rather than
    // resizing it. Needs both endpoints set before the edit to know the span.
    if (preserveDuration && value.startDate && value.endDate) {
      const spanMs = Date.parse(value.endDate) - Date.parse(value.startDate);
      if (key === 'startDate' && next.startDate) {
        next.endDate = toUtcDayEnd(
          new Date(Date.parse(next.startDate) + spanMs)
        ).toISOString();
      } else if (key === 'endDate' && next.endDate) {
        next.startDate = toUtcDayStart(
          new Date(Date.parse(next.endDate) - spanMs)
        ).toISOString();
      }
      setActivePreset('custom');
      onChange?.(next);
      return;
    }
    // An end date can never precede the start. If editing one side inverts the
    // range, collapse the other side onto the same day so the window stays
    // valid (a single-day range) rather than producing a negative duration.
    if (next.startDate && next.endDate) {
      const startMs = Date.parse(next.startDate);
      const endMs = Date.parse(next.endDate);
      if (endMs < startMs) {
        const day = new Date(`${iso}T00:00:00.000Z`);
        if (key === 'startDate') {
          next.endDate = toUtcDayEnd(day).toISOString();
        } else {
          next.startDate = toUtcDayStart(day).toISOString();
        }
      }
    }
    setActivePreset('custom');
    onChange?.(next);
  };

  // Inline-calendar day click. `dayKey` is a `YYYY-MM-DD`.
  const handleCalendarDay = (dayKey: string) => {
    const day = new Date(`${dayKey}T00:00:00.000Z`);
    const dayStart = toUtcDayStart(day).toISOString();
    const dayEnd = toUtcDayEnd(day).toISOString();
    setActivePreset('custom');

    // Fixed-duration move: keep the day-count and translate the window so
    // the clicked day becomes the new start.
    if (preserveDuration && value.startDate && value.endDate) {
      const spanMs = Date.parse(value.endDate) - Date.parse(value.startDate);
      onChange?.({
        startDate: dayStart,
        endDate: toUtcDayEnd(
          new Date(Date.parse(dayStart) + spanMs)
        ).toISOString(),
      });
      return;
    }

    // Two-click range build: first click (or a click before the open start)
    // sets a fresh start; the next click on/after it closes the range.
    if (value.startDate && !value.endDate) {
      if (dayKey >= value.startDate.slice(0, 10)) {
        onChange?.({ startDate: value.startDate, endDate: dayEnd });
      } else {
        onChange?.({ startDate: dayStart, endDate: undefined });
      }
      return;
    }
    onChange?.({ startDate: dayStart, endDate: undefined });
  };

  const handleClear = (e: ReactMouseEvent) => {
    e.stopPropagation();
    onChange?.({});
    setActivePreset('custom');
  };

  const display = formatDisplay(value);
  const hasValue = Boolean(value.startDate && value.endDate);

  return (
    <div className={css.wrapper} ref={rootRef} style={style}>
      <button
        className={styles(
          { isDisabled: !!disabled, isOpen: open, size },
          className
        )}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        type="button"
      >
        <Calendar className={css.leadingIcon} />
        <span className={css.value}>{display ?? placeholder}</span>
        {hasValue && (
          <X
            aria-label="Clear date range"
            className={css.clear}
            onClick={handleClear}
          />
        )}
        <ChevronDown
          className={joinCss(css.chevron, open && css.chevronOpen)}
        />
      </button>

      {open && (
        <div
          ref={dropdownRef}
          className={css.dropdown}
          style={
            shift !== 0 ? { transform: `translateX(${shift}px)` } : undefined
          }
        >
          <div className={css.presets}>
            {presets.map((preset) => (
              <button
                className={joinCss(
                  css.preset,
                  activePreset === preset && css.presetActive
                )}
                key={preset}
                onClick={() => applyPreset(preset)}
                type="button"
              >
                {PRESET_LABEL[preset]}
              </button>
            ))}
          </div>
          {/* No cross-field min/max on the native inputs: they would disable
              the calendar past the other endpoint and trap "move the range
              forward" (a future start can't be picked while it sits after the
              current end). `handleCustomChange` already keeps the range valid
              by collapsing the inverted side onto the same day. */}
          <div className={css.customRow}>
            <label className={css.customField}>
              <span>From</span>
              <input
                onChange={(e) =>
                  handleCustomChange('startDate', e.target.value)
                }
                type="date"
                value={isoToInputValue(value.startDate)}
              />
            </label>
            <label className={css.customField}>
              <span>To</span>
              <input
                onChange={(e) => handleCustomChange('endDate', e.target.value)}
                type="date"
                value={isoToInputValue(value.endDate)}
              />
            </label>
          </div>
          {showCalendar && (
            <RangeCalendar
              endDate={value.endDate}
              onSelectDay={handleCalendarDay}
              startDate={value.startDate}
            />
          )}
        </div>
      )}
    </div>
  );
};
